import * as React from 'react';
import { MarketButton, MarketCheckbox, MarketHeader, MarketInputSearch, MarketLink, MarketList, MarketModalPartial, MarketRow } from '@market/react-bindings';
import { InModal } from '@goparrot-dashboard/shared-ui';
import { IReadDefaultStoreDto, IReadStoreDto } from '@goparrot/store-v2-sdk';
import { useI18n } from '@goparrot-dashboard/i18n';
import classNames from 'classnames';
import { useDialogState } from '../index';

type MarketLocationSelectProps = {
  value: string[];
  locations: (IReadStoreDto | IReadDefaultStoreDto)[];
  hasError?: boolean;
  onChange(ids: string[]): void;
};

export type MarketLocationSelectModalProps = {
  onDismiss(): void;
  onSubmit(storeIds: string[]): void;
  list: (IReadStoreDto | IReadDefaultStoreDto)[];
  selected: string[];
  disabled?: boolean;
};

type InternalComponents = {
  Modal: React.FC<MarketLocationSelectModalProps>;
};

export const MarketLocationSelect: React.FC<MarketLocationSelectProps> & InternalComponents = ({ locations, value: selected, hasError, onChange }) => {
  const { getIntlString } = useI18n();
  const dialog = useDialogState();

  const handleSubmit = (storeIds: string[]) => {
    onChange(storeIds);

    dialog.dismiss();
  };

  const getLocationSubtext = () => {
    switch (selected.length) {
      case 1: {
        const [storeId] = selected;
        return locations.find((l) => l.storeId === storeId)?.title ?? storeId;
      }
      case 0: {
        return getIntlString('location.select.noLocations', { isRootPath: true });
      }
      default: {
        return getIntlString('location.select.number', { isRootPath: true, values: { number: selected.length } });
      }
    }
  };

  return (
    <div className={classNames([hasError && 'tw-border tw-border-red-400 tw-rounded-md'])}>
      <MarketRow size="medium" interactive transient onClick={dialog.show}>
        <label slot="label">{getIntlString('location.select.title', { isRootPath: true })}</label>
        <p className="tw-m-0" slot="subtext">
          {getLocationSubtext()}
        </p>
        <MarketLink slot="trailing-accessory" onClick={dialog.show}>
          {getIntlString('button.edit', { isRootPath: true })}
        </MarketLink>
      </MarketRow>
      {dialog.visible && <MarketLocationSelect.Modal onDismiss={dialog.dismiss} onSubmit={handleSubmit} list={locations} selected={selected} />}
    </div>
  );
};

type LocationSelectOptionType = {
  value: string;
  label: string;
  isDeleted?: boolean;
};

const ModalContent: React.FC<MarketLocationSelectModalProps> = ({ onDismiss, onSubmit, list, selected, disabled }) => {
  const { getIntlString } = useI18n();
  const [selection, setSelection] = React.useState(selected);
  const options = React.useMemo<LocationSelectOptionType[]>(() => {
    const missedStores = selected
      .filter((storeId) => !list.some((store) => store.storeId === storeId))
      .map((storeId) => ({ value: storeId, label: '#Not found location', isDeleted: true }));
    const stores = list.map((option) => ({ value: option.storeId, label: option.title }));

    return [...missedStores, ...stores];
  }, [list, selected]);

  const handleSelect = (storeId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelection(selection.filter((id) => id !== storeId));
      return;
    }
    setSelection([...selection, storeId]);
  };

  const handleSelectAll = () => {
    if (selection.length >= options.length) {
      setSelection([]);
      return;
    }
    setSelection(options.map((o) => o.value));
  };

  const handleSubmit = () => {
    onSubmit(selection);
  };

  return (
    <InModal>
      <MarketModalPartial onMarketDialogDismissed={onDismiss}>
        <MarketHeader>
          <h2>{getIntlString('location.select.edit', { isRootPath: true })}</h2>
          <MarketButton rank="primary" slot="actions" onClick={handleSubmit}>
            {getIntlString('button.done', { isRootPath: true })}
          </MarketButton>
        </MarketHeader>
        <main>
          <MarketList slot="content" multiselect interactive value={selection}>
            <MarketInputSearch slot="search" />
            <MarketRow className="tw-mx-0" value="all" slot="control-row" onClick={handleSelectAll}>
              <label slot="label">{getIntlString('location.select.title', { isRootPath: true })}</label>
              <MarketCheckbox slot="control" />
            </MarketRow>
            {options.map((option) => {
              const { value, label, isDeleted } = option;
              const isSelected = selection.includes(value);
              const isDisabled = disabled || (isDeleted && !isSelected);

              return (
                <MarketRow
                  key={value}
                  className="tw-mx-0"
                  value={value}
                  disabled={isDisabled || undefined}
                  interactive
                  onClick={() => handleSelect(value, isSelected)}
                >
                  <label slot="label">{label}</label>
                  {isDeleted && <span slot="subtext">{value}</span>}
                  <MarketCheckbox slot="control" />
                </MarketRow>
              );
            })}
          </MarketList>
        </main>
      </MarketModalPartial>
    </InModal>
  );
};

MarketLocationSelect.Modal = ModalContent;
